import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import CarCard from '../components/CarCard'
import TestimonialSlider from '../components/TestimonialSlider'
import GoogleMap from '../components/GoogleMap'
import { getResizedImageUrl } from '../lib/utils'

function CountingNumber({ value, suffix = "", duration = 1500 }) {
  const [count, setCount] = useState(0);
  const [element, setElement] = useState(null);

  useEffect(() => {
    if (!element) return;

    let animated = false;
    let animationFrameId;

    const startAnimation = () => {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const ease = progress * (2 - progress); // Ease out quad
        setCount(Math.floor(ease * value));
        if (progress < 1) {
          animationFrameId = window.requestAnimationFrame(step);
        } else {
          setCount(value);
        }
      };
      animationFrameId = window.requestAnimationFrame(step);
    };

    if (typeof IntersectionObserver === 'undefined') {
      startAnimation();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated) {
          animated = true;
          observer.unobserve(element);
          startAnimation();
        }
      },
      { threshold: 0.05 }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [element, value, duration]);

  return (
    <span ref={setElement}>
      {count}
      {suffix}
    </span>
  );
}

export default function Home() {
  const [cars, setCars] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/_api/public/content/lagerbiler')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setCars(data)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af biler:', err)
        setLoading(false)
      })
  }, [])

  const featuredCars = cars.slice(0, 3)

  const serviceGrid = [
    {
      title: 'Klargøring',
      desc: 'Professionel kosmetisk og teknisk klargøring af alle biler.',
      img: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/service-detailing.jpg',
      link: '/ydelse'
    },
    {
      title: 'Køb & Salg',
      desc: 'Håndplukket udvalg af brugte kvalitetsbiler til skarpe priser.',
      img: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c3e6961c-2052-4b9b-9e42-5b2710dd0def.jpeg',
      link: '/biler-til-salg'
    },
    {
      title: 'Finansiering',
      desc: 'Attraktive finansieringsløsninger med og uden udbetaling.',
      img: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/service-financing.jpg',
      link: '/finansiering'
    },
    {
      title: 'Rådgivning',
      desc: 'Vi tager os tid til at finde den helt rette bil til dine behov.',
      img: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/service-consultation.jpg',
      link: '/kontakt'
    }
  ]

  const [activeReason, setActiveReason] = useState(0);

  const reasons = [
    {
      number: "",
      title: "Hvorfor RECARS?",
      subtitle: "3 gode grunde",
      description: "Vi stræber efter at levere den ultimative biloplevelse med fokus på kvalitet, gennemsigtighed og uovertruffen personlig service.",
      ctaText: "Se vores biler",
      ctaLink: "/biler-til-salg",
      image: "https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c601a899-b39d-42a3-bfb8-8db23a717738.jpeg"
    },
    {
      number: "01",
      title: "Kvalitetsbiler",
      subtitle: "Håndplukket udvalg",
      description: "Vi har et spændende udvalg af kvalitetsbiler. Hver bil gennemgår et omfattende kvalitetstjek inden salg.",
      ctaText: "Se biler til salg",
      ctaLink: "/biler-til-salg",
      image: "https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c3e6961c-2052-4b9b-9e42-5b2710dd0def.jpeg"
    },
    {
      number: "02",
      title: "De bedste priser",
      subtitle: "Mest værdi for pengene",
      description: "Vi har landets absolutte bedste priser. Vi sikrer dig mest værdi for pengene uden at gå på kompromis med kvaliteten.",
      ctaText: "Beregn finansiering",
      ctaLink: "/finansiering",
      image: "https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/1dc339cb-fcbf-4442-b57c-579c89b41801.jpeg"
    },
    {
      number: "03",
      title: "Tryg og sikker handel",
      subtitle: "Mange års erfaring",
      description: "Vi har mange års erfaring med salg og håndtering af brugte biler, hvilket sikrer dig som kunde en seriøs, tryg og sikker handel.",
      ctaText: "Kontakt os i dag",
      ctaLink: "/kontakt",
      image: "https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/service-consultation.jpg"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Kvalitetsbiler nær Vejle, Kolding og Fredericia | Recars ApS</title>
        <meta name="description" content="Kvalitetsbiler nær Vejle, Kolding og Fredericia. Kontakt Recars ApS i dag!" />
        <meta property="og:title" content="Kvalitetsbiler nær Vejle, Kolding og Fredericia | Recars ApS" />
        <meta property="og:description" content="Kvalitetsbiler nær Vejle, Kolding og Fredericia. Kontakt Recars ApS i dag!" />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c601a899-b39d-42a3-bfb8-8db23a717738.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://recars.dk/" />
      </Helmet>

      {/* Section 1: Hero Header Area */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-neutral-950 pt-24 pb-16">
        {/* Back Layer (z-0): Full-width, low-contrast background video */}
        <div className="absolute inset-0 z-0">
          <video
            src="https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/gettyimages-1413058725-1920x1080-6000k.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-60 filter brightness-90 contrast-110"
          />
        </div>

        {/* Hero Stage Container */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          
          {/* Middle Layer (z-10): Bounding box with vibrant accent border and glassmorphism */}
          <div className="relative w-full mt-16 border-2 border-accent/40 bg-white/5 backdrop-blur-md p-6 sm:p-12 md:p-16 lg:p-20 shadow-2xl flex flex-col items-center text-center">
            
            {/* Content Text Block (z-20) */}
            <div className="relative z-20 text-center max-w-2xl">
              <span className="font-bold text-accent tracking-widest text-xs sm:text-sm uppercase block mb-3">
                KVALITETSBILER NÆR VEJLE, KOLDING OG FREDERICIA
              </span>
              
              <h1 className="font-display font-light uppercase tracking-tight text-3xl sm:text-5xl md:text-6xl text-white leading-tight">
                Velkommen til Recars ApS
              </h1>
              
              <p className="font-sans font-normal text-neutral-300 text-sm sm:text-base md:text-lg leading-relaxed mt-4 mb-8">
                Nøglen til en tryg og sikker bilhandel får du igennem os.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/biler-til-salg"
                  className="px-6 sm:px-8 py-3.5 bg-accent text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-neutral-950 transition-all duration-300 rounded-none shadow-lg shadow-accent/20"
                >
                  Se biler til salg
                </Link>
                <Link
                  to="/kontakt"
                  className="px-6 sm:px-8 py-3.5 border-2 border-white text-xs font-bold uppercase tracking-widest text-white hover:bg-accent hover:border-accent hover:text-neutral-950 transition-all duration-300 rounded-none"
                >
                  Kontakt os i dag
                </Link>
              </div>
            </div>

          </div>
        </div>

        {/* Action Cue (Scroll Down) */}
        <div className="absolute bottom-8 right-6 md:right-12 z-10 hidden sm:flex items-center gap-2 text-neutral-400 text-xs uppercase tracking-[0.2em] select-none">
          <span>Rul ned</span>
          <i className="fa-solid fa-arrow-down-long animate-bounce text-accent" />
        </div>
      </section>

      {/* Section 3: Brand Statement Space - Lys & Imødekommende */}
      <section className="py-28 md:py-36 flex items-center justify-center bg-[#F8F6F0] relative overflow-hidden border-y border-[#E8E5DE]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(223,181,135,0.15),transparent_70%)] pointer-events-none" />
        <h2 className="text-stone-300 font-extrabold text-4xl md:text-[8rem] uppercase tracking-widest select-none font-display text-center leading-none relative z-10">
          TRYG BILHANDEL
        </h2>
      </section>

      {/* New Section: Kvalitetsbiler nær Vejle, Kolding og Fredericia - Lys sektion */}
      <section className="py-24 bg-[#FAF9F6] border-b border-stone-200 relative overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 xl:px-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            {/* Left Image Column */}
            <div className="lg:col-span-6 relative group">
              <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl">
                <img
                  src={getResizedImageUrl('https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/1dc339cb-fcbf-4442-b57c-579c89b41801.jpeg', 800)}
                  alt="Recars kvalitetsbiler"
                  className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            {/* Right Text Column */}
            <div className="lg:col-span-6 text-left">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#9b784a] block mb-3">
                Velkommen til Recars ApS
              </span>
              <h2 className="text-3xl sm:text-5xl font-light uppercase tracking-tight text-neutral-900 font-display leading-tight mb-6">
                Kvalitetsbiler nær Vejle, Kolding og Fredericia
              </h2>
              <p className="text-base sm:text-lg text-neutral-800 font-medium leading-relaxed mb-6">
                Nøglen til en tryg og sikker bilhandel får du igennem os.
              </p>
              <div className="space-y-4 text-sm text-neutral-600 leading-relaxed mb-8">
                <p>
                  Hos Recars ApS stræber vi efter at levere den absolut bedste oplevelse, uanset om du køber eller sælger din bil. Vi udvælger kun de bedste biler, så du kan køre herfra med ro i maven.
                </p>
                <p>
                  Recars i Børkop er centralt placeret i trekantområdet, hvilket gør det nemt for kunder fra både Vejle, Kolding og Fredericia at besøge os og opleve vores eksklusive udvalg af topklargjorte biler.
                </p>
              </div>
              <div>
                <Link
                  to="/biler-til-salg"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-accent text-neutral-950 text-xs font-bold uppercase tracking-widest hover:bg-neutral-950 hover:text-white transition-all duration-300 rounded-none shadow-lg shadow-accent/20"
                >
                  Se biler til salg <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Vibrant Accent Banner */}
      <section className="py-16 bg-gradient-to-r from-accent to-accent-hover relative overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,white_10%,transparent_70%)]" />
        
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 xl:px-16 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div className="flex flex-col items-center justify-center">
            <span className="text-4xl md:text-6xl font-light text-white font-display tracking-tight">
              <CountingNumber value={10} suffix="+" /> ÅR
            </span>
            <span className="text-xs md:text-sm font-bold tracking-widest text-white/90 uppercase mt-2">
              Erfaring i branchen
            </span>
          </div>
          <div className="flex flex-col items-center justify-center">
            <span className="text-4xl md:text-6xl font-light text-white font-display tracking-tight">
              <CountingNumber value={100} suffix="%" />
            </span>
            <span className="text-xs md:text-sm font-bold tracking-widest text-white/90 uppercase mt-2">
              Kvalitetstjekket
            </span>
          </div>
          <div className="flex flex-col items-center justify-center">
            <span className="text-4xl md:text-6xl font-light text-white font-display tracking-tight">
              <CountingNumber value={5} /> / 5
            </span>
            <span className="text-xs md:text-sm font-bold tracking-widest text-white/90 uppercase mt-2">
              Stjerner på Trustpilot
            </span>
          </div>
          <div className="flex flex-col items-center justify-center">
            <span className="text-4xl md:text-6xl font-light text-white font-display tracking-tight">
              <CountingNumber value={24} suffix="/7" />
            </span>
            <span className="text-xs md:text-sm font-bold tracking-widest text-white/90 uppercase mt-2">
              Personlig service
            </span>
          </div>
        </div>
      </section>

      {/* Section 4: Dual-Column Call-to-Action (CTA) Cards */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 w-full bg-neutral-950 border-t border-neutral-900">
        {/* Left Column: Find Car - Rigtigt foto af Kia EV6 elbil */}
        <div className="relative h-[450px] md:h-[550px] flex flex-col justify-center items-center text-center px-6 md:px-12 overflow-hidden group">
          <img
            src={getResizedImageUrl('https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c601a899-b39d-42a3-bfb8-8db23a717738.jpeg', 1000)}
            alt="Find din næste bil - Kia EV6"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 z-0"
          />
          <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-500 z-10" />
          
          <div className="relative z-20 max-w-md flex flex-col items-center">
            <span className="text-xs font-bold tracking-[0.3em] text-accent uppercase mb-3">
              STORT UDVALG
            </span>
            <h3 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight font-display mb-4">
              UDVALGTE LAGERBILER
            </h3>
            <p className="text-sm text-neutral-200 mb-8 leading-relaxed">
              Udforsk vores håndplukkede udvalg af nyere brugte kvalitetsbiler. Vi garanterer topklargjorte biler til skarpe priser.
            </p>
            <Link
              to="/biler-til-salg"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/80 text-xs font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black transition-all duration-300 rounded-none shadow-lg"
            >
              Se alle biler
            </Link>
          </div>
        </div>

        {/* Right Column: Sell Car - Rigtigt foto af BMW iX3 elbil */}
        <div className="relative h-[450px] md:h-[550px] flex flex-col justify-center items-center text-center px-6 md:px-12 overflow-hidden group border-t md:border-t-0 md:border-l border-neutral-900">
          <img
            src={getResizedImageUrl('https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/c3e6961c-2052-4b9b-9e42-5b2710dd0def.jpeg', 1000)}
            alt="Sælg din bil - BMW iX3"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 z-0"
          />
          <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-500 z-10" />
          
          <div className="relative z-20 max-w-md flex flex-col items-center">
            <span className="text-xs font-bold tracking-[0.3em] text-accent uppercase mb-3">
              HURTIG HANDEL
            </span>
            <h3 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight font-display mb-4">
              FÅ ET UFORPLIGTENDE BUD
            </h3>
            <p className="text-sm text-neutral-200 mb-8 leading-relaxed">
              Overvejer du at sælge din nuværende bil? Vi køber nyere brugte biler i ren handel til en fair markedspris.
            </p>
            <Link
              to="/kontakt"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/80 text-xs font-bold uppercase tracking-widest text-white hover:bg-white hover:text-black transition-all duration-300 rounded-none shadow-lg"
            >
              Få et tilbud nu
            </Link>
          </div>
        </div>
      </section>

      {/* Section 7: 3 Gode Grunde - Interactive Accordion */}
      <section className="w-full bg-neutral-950 border-t border-neutral-900 overflow-hidden">
        <div className="flex flex-col md:flex-row w-full h-auto md:h-[480px] gap-0">
          {reasons.map((reason, index) => {
            const isActive = activeReason === index;
            return (
              <div
                key={index}
                onClick={() => setActiveReason(index)}
                className={`relative overflow-hidden transition-all duration-700 ease-in-out cursor-pointer group rounded-none border-none flex flex-col justify-between
                  ${isActive 
                    ? 'flex-[3.5] bg-[#DFB587] text-neutral-950 h-[480px] md:h-full' 
                    : 'flex-1 bg-neutral-900 text-white h-[130px] md:h-full hover:bg-neutral-800'
                  }`}
              >
                {/* Background Image for Collapsed State */}
                {!isActive && (
                  <div className="absolute inset-0 z-0 transition-opacity duration-700 opacity-30 group-hover:opacity-50">
                    <img
                      src={`${reason.image}?width=800&quality=80&format=auto`}
                      alt={reason.title}
                      className="w-full h-full object-cover filter grayscale contrast-125"
                    />
                    <div className="absolute inset-0 bg-neutral-950/80" />
                  </div>
                )}

                {/* Oversized Display Number - kun hvis number findes */}
                {reason.number && (
                  <div className={`absolute bottom-0 right-0 translate-y-4 translate-x-2 md:translate-y-8 md:translate-x-4 font-display font-black text-7xl md:text-[8rem] leading-none select-none pointer-events-none z-10 transition-colors duration-500
                    ${isActive ? 'text-white/20' : 'text-white/10'}`}
                  >
                    {reason.number}
                  </div>
                )}

                {/* Active Content */}
                {isActive ? (
                  <div className="relative z-10 flex flex-col justify-between h-full p-8 md:p-12 select-none">
                    {/* Top left: Heading */}
                    <div className={`transition-all duration-700 delay-100 transform ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-950/60 block mb-2">
                        {reason.subtitle}
                      </span>
                      <h3 className="text-2xl md:text-4xl font-medium uppercase tracking-tight font-display text-neutral-950 leading-tight">
                        {reason.title}
                      </h3>
                    </div>

                    {/* Bottom left: Body text and CTA */}
                    <div className={`mt-auto max-w-md transition-all duration-700 delay-200 transform ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                      <p className="text-sm md:text-base text-neutral-900 font-sans leading-relaxed mb-6">
                        {reason.description}
                      </p>
                      <Link
                        to={reason.ctaLink}
                        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-neutral-950 hover:underline group/btn"
                      >
                        <span>{reason.ctaText}</span>
                        <i className="fa-solid fa-arrow-right transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  /* Collapsed Content */
                  <>
                    {/* Desktop Collapsed: Rotated Title */}
                    <div className="absolute inset-0 hidden md:flex items-center justify-center z-10">
                      <div className="-rotate-90 whitespace-nowrap">
                        <h3 className="text-lg font-medium uppercase tracking-[0.2em] font-display text-white/80 group-hover:text-white transition-colors">
                          {reason.title}
                        </h3>
                      </div>
                    </div>

                    {/* Mobile Collapsed: Horizontal Row */}
                    <div className="flex md:hidden items-center justify-between w-full h-full px-6 relative z-10">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/40 block">
                          {reason.subtitle}
                        </span>
                        <h3 className="text-lg font-bold uppercase tracking-wider font-display text-white">
                          {reason.title}
                        </h3>
                      </div>
                      {reason.number && (
                        <span className="text-2xl font-black font-display text-white/30">
                          {reason.number}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 8: Testimonials */}
      <TestimonialSlider />

      {/* Section 9: Google Maps Section */}
      <GoogleMap />

      {/* Section: Partner Logo Slider - Lys & Elegant */}
      <section className="py-16 bg-[#FAF9F6] border-t border-stone-200 overflow-hidden">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12 text-center mb-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9b784a]">
            Samarbejdspartnere
          </span>
          <h2 className="mt-2 text-2xl font-light uppercase tracking-wider text-neutral-900 font-display">
            Vi samarbejder med
          </h2>
        </div>

        <div className="relative flex w-full overflow-x-hidden py-4 bg-transparent border-y border-stone-200">
          {/* Gradient overlays for premium fade effect */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 md:w-32 bg-gradient-to-r from-[#FAF9F6] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 md:w-32 bg-gradient-to-l from-[#FAF9F6] to-transparent" />

          <div className="infinite-scroll-track flex items-center gap-16 md:gap-24">
            {Array(6).fill([
              { src: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/hjemmeside-kommentarer-pdf-image-009.png', alt: 'Partner Logo 1' },
              { src: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/tryg-logo.png', alt: 'Tryg Forsikring' },
              { src: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/logo-santander.png', alt: 'Santander Consumer Bank' },
              { src: 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/hjemmeside-kommentarer-pdf-image-003.png', alt: 'Partner Logo 2' }
            ]).flat().map((logo, i) => (
              <div key={i} className="h-20 w-64 shrink-0 flex items-center justify-center">
                <img
                  src={getResizedImageUrl(logo.src, 300)}
                  alt={logo.alt}
                  className="h-12 md:h-16 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
